// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'isOpen' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';


// -------------------------------------
// Variables
// -------------------------------------

const ACCORDION                = '.js-accordion';
const ACCORDION_BUTTON         = '.js-accordion__button';
const ACCORDION_ITEM           = '.js-accordion__item';
const ACCORDION_CONTENT        = '.js-accordion__content';
const ACCORDION_TITLE          = '.accordion__title';
const ACCORDION_CONTENT_MOBILE = '.js-accordion__content--mobile-only';

// Constant trigger classes
const OPEN_CLASS          = 'is-open';



// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
    e.setAttribute('tabindex', value);
}

function toggleAccordion(accordion) {

    const element = (accordion.item.parentElement.dataset.autoCollapse != 'false') ? accordion.item.parentElement : accordion.item;

    element.querySelectorAll('.' + OPEN_CLASS).forEach(function(e) { 
        e.classList.remove(OPEN_CLASS);
    });

    element.querySelectorAll(ACCORDION_CONTENT).forEach(function(e) {
        e.hidden = true;
        e.style.height = null;
    });

    element.querySelectorAll(ACCORDION_BUTTON).forEach(function(e) {
        e.setAttribute('aria-expanded', 'false');
    });

    element.querySelectorAll(ACCORDION_CONTENT).forEach(function(e) {
        e.querySelectorAll('a').forEach(el => {
            setTabIndex(el, -1);
        });
    });

    if (accordion.isOpening) {
        accordion.button.classList.add(OPEN_CLASS);
        accordion.item.classList.add(OPEN_CLASS);
        accordion.content.classList.add(OPEN_CLASS);
        accordion.content.style.height = accordion.content.scrollHeight + "px";

        accordion.button.setAttribute('aria-expanded', 'true');
        accordion.content.hidden = false;
        accordion.content.querySelectorAll('a').forEach(function(el) {
            setTabIndex(el, 0);
        });
    }
}

function accordionEventHandle(e) {
    // Store elements as an object
    const accordion = {
        button:    e.target,
        item:      e.target.closest(ACCORDION_ITEM),
        title:     e.target.closest(ACCORDION_TITLE),
        content:   e.target.closest(ACCORDION_TITLE).nextElementSibling,
        isOpening:  !e.target.classList.contains(OPEN_CLASS),
        container: e.target.closest(ACCORDION),
        button_group: e.target.closest(ACCORDION).querySelectorAll(ACCORDION_BUTTON)
    };

    // Collapse any open sibling & children accordions if data attribute set to true
    if (e.type === 'click') {
        e.preventDefault();
        toggleAccordion(accordion);
    } else if (e.type === 'keydown') {
        const key = e.which.toString();
        if (key.match(/35|36|38|40/)) {
            e.preventDefault();
            const index = Array.prototype.indexOf.call(accordion.button_group, e.target);
            const length = accordion.button_group.length;
            let newIndex;
            switch(key) {
                case '38': // Up
                    newIndex = (index + length + -1) % length;
                    accordion.button_group[newIndex].focus();
                    break;
                case '40': // Down
                    newIndex = (index + length + 1) % length;
                    accordion.button_group[newIndex].focus();
                    break;
                case '35': // End
                    accordion.button_group[length - 1].focus();
                    break;
                case '36': // Home
                    accordion.button_group[0].focus();
                    break;
            }
        }
    }
}

// -------------------------------------
// Hooks
// -------------------------------------

document.addEventListener('DOMContentLoaded', function() {
    let accordionContentLinks;
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) { // On mobile, find all accordion content links to add tabindex -1
        accordionContentLinks = document.querySelectorAll(ACCORDION_CONTENT + ' a');
    } else { // If on desktop, find all accordion content links that don't belong to the #accordion-cart--content on the cart page.
        accordionContentLinks = document.querySelectorAll(`${ACCORDION_CONTENT}:not(${ACCORDION_CONTENT_MOBILE}) a`);
    }
    const accordionButtons = document.querySelectorAll(ACCORDION_BUTTON);

    accordionContentLinks.forEach(function(e) {
        setTabIndex(e, -1);
    });

    accordionButtons.forEach(function(e) {
        e.addEventListener('click', accordionEventHandle);
        e.addEventListener('keydown', accordionEventHandle);
    });
});

window.addEventListener("resize", function(e) {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 1024) {
        // On mobile, find all accordion content links of the close accordions to add tabindex -1
        const accordionContentLinks = document.querySelectorAll(`${ACCORDION_CONTENT}:not(.${OPEN_CLASS}) a`);
        accordionContentLinks.forEach(function(e) {
            setTabIndex(e, -1);
        });
    } else {
        // If on desktop, find all accordion content links that belongs to the #accordion-cart--content on the cart page to remove tabindex -1.
        const accordionCartContentLinks = document.querySelectorAll(`${ACCORDION_CONTENT}${ACCORDION_CONTENT_MOBILE} a`);
        accordionCartContentLinks.forEach(function(e) {
            setTabIndex(e, 0);
        });
    }

    // Re-adjust all the expanded accordion content tab height on "resize" event by setting their height to "auto"
    const expandedAccordionContent = document.querySelectorAll(`${ACCORDION_CONTENT}.${OPEN_CLASS}`);
    expandedAccordionContent.forEach(function(accordionContent) {
        accordionContent.style.height = "auto";
    })
});
